import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Paper,
  Rating, Collapse, Tabs, Tab, Menu, Tooltip, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';

const MEAL_TYPES = {
  breakfast: { label: 'Breakfast', color: '#ffa726' },
  lunch:     { label: 'Lunch',     color: '#42a5f5' },
  dinner:    { label: 'Dinner',    color: '#7e57c2' },
  dessert:   { label: 'Dessert',   color: '#ec407a' },
  snack:     { label: 'Snack',     color: '#66bb6a' },
  drink:     { label: 'Drink',     color: '#26c6da' },
  other:     { label: 'Other',     color: '#9e9e9e' },
};

const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// ─── Add/Edit Recipe Dialog ───────────────────────────────────────────────────

const RecipeDialog = ({ open, onClose, onSave, existing }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('dinner');
  const [cuisine, setCuisine] = useState('');
  const [description, setDescription] = useState('');
  const [servings, setServings] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [ingredients, setIngredients] = useState('');
  const [instructions, setInstructions] = useState('');

  React.useEffect(() => {
    if (open) {
      setName(existing?.name || '');
      setType(existing?.type || 'dinner');
      setCuisine(existing?.cuisine || '');
      setDescription(existing?.description || '');
      setServings(existing?.servings || '');
      setPrepTime(existing?.prepTime || '');
      setSourceUrl(existing?.sourceUrl || '');
      setIngredients(existing?.ingredients || '');
      setInstructions(existing?.instructions || '');
    }
  }, [open, existing]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), type, cuisine, description, servings, prepTime, sourceUrl, ingredients, instructions });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{existing ? 'Edit Recipe' : 'Add Recipe'}</DialogTitle>
      <DialogContent>
        <TextField label="Recipe Name" fullWidth value={name} onChange={e => setName(e.target.value)}
          sx={{ mb: 2, mt: 1 }} required autoFocus />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl sx={{ flex: 1 }}>
            <InputLabel>Type</InputLabel>
            <Select value={type} onChange={e => setType(e.target.value)} label="Type">
              {Object.entries(MEAL_TYPES).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  <Chip label={v.label} size="small" sx={{ bgcolor: `${v.color}22`, color: v.color, border: `1px solid ${v.color}44` }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Cuisine" sx={{ flex: 1 }} value={cuisine}
            onChange={e => setCuisine(e.target.value)} placeholder="e.g. Italian, Japanese" />
        </Box>
        <TextField label="Description" fullWidth multiline rows={2} value={description}
          onChange={e => setDescription(e.target.value)} placeholder="A short description of the dish..."
          sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField label="Servings" value={servings} onChange={e => setServings(e.target.value)}
            placeholder="e.g. 4" sx={{ flex: 1 }} />
          <TextField label="Prep / Cook Time" value={prepTime} onChange={e => setPrepTime(e.target.value)}
            placeholder="e.g. 30 mins" sx={{ flex: 1 }} />
        </Box>
        <TextField label="Source URL" fullWidth value={sourceUrl} onChange={e => setSourceUrl(e.target.value)}
          placeholder="Link to original recipe (optional)"
          InputProps={{ startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ mb: 2 }} />
        <TextField label="Ingredients" fullWidth multiline rows={4} value={ingredients}
          onChange={e => setIngredients(e.target.value)}
          placeholder={"200g flour\n2 eggs\n1 cup milk\n..."}
          sx={{ mb: 2 }} />
        <TextField label="Instructions" fullWidth multiline rows={5} value={instructions}
          onChange={e => setInstructions(e.target.value)}
          placeholder={"1. Mix the dry ingredients...\n2. Add eggs and milk...\n..."} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!name.trim()}>
          {existing ? 'Save' : 'Add Recipe'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Recipe Card ──────────────────────────────────────────────────────────────

const RecipeCard = ({ recipe, onUpdate, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reviewRating, setReviewRating] = useState(recipe.rating || 0);
  const [reviewNotes, setReviewNotes] = useState(recipe.review || '');
  const [menuAnchor, setMenuAnchor] = useState(null);

  const typeMeta = MEAL_TYPES[recipe.type] || MEAL_TYPES.other;

  const saveReview = () => {
    onUpdate({ ...recipe, made: true, madeDate: new Date().toISOString().slice(0, 10), rating: reviewRating, review: reviewNotes });
    setReviewing(false);
  };

  const openReview = () => {
    setReviewRating(recipe.rating || 0);
    setReviewNotes(recipe.review || '');
    setReviewing(true);
    setExpanded(true);
  };

  return (
    <Paper elevation={0} sx={{
      mb: 2, borderRadius: 2, overflow: 'hidden',
      bgcolor: 'rgba(255,255,255,0.04)',
      border: `1px solid ${recipe.made ? 'rgba(102,187,106,0.2)' : 'rgba(255,255,255,0.06)'}`,
      '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
    }}>
      {/* Card header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, pb: expanded ? 1 : 2 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ maxWidth: { xs: 160, sm: 300 } }}>
              {recipe.name}
            </Typography>
            <Chip label={typeMeta.label} size="small"
              sx={{ bgcolor: `${typeMeta.color}22`, color: typeMeta.color, border: `1px solid ${typeMeta.color}44`, fontSize: '0.65rem' }} />
            {recipe.cuisine && (
              <Chip label={recipe.cuisine} size="small" variant="outlined" sx={{ fontSize: '0.65rem', color: 'text.secondary' }} />
            )}
          </Box>
          {recipe.description && (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.3 }}>
              {recipe.description}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
            {recipe.servings && <Typography variant="caption" color="text.secondary">🍽 {recipe.servings} servings</Typography>}
            {recipe.prepTime && <Typography variant="caption" color="text.secondary">⏱ {recipe.prepTime}</Typography>}
            {recipe.made ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 14, color: '#66bb6a' }} />
                <Typography variant="caption" sx={{ color: '#66bb6a' }}>Made</Typography>
                {recipe.rating > 0 && <Rating value={recipe.rating} readOnly size="small" sx={{ fontSize: 14 }} />}
              </Box>
            ) : (
              <Typography variant="caption" color="text.disabled">Not made yet</Typography>
            )}
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <IconButton size="small" onClick={() => onUpdate({ ...recipe, favorite: !recipe.favorite })}
            sx={{ color: recipe.favorite ? '#ef5350' : 'text.disabled' }}>
            {recipe.favorite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={e => setMenuAnchor(e.currentTarget)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Expanded content */}
      <Collapse in={expanded}>
        <Divider sx={{ mx: 2 }} />
        <Box sx={{ p: 2, pt: 1.5 }}>
          {/* Source URL */}
          {recipe.sourceUrl && (
            <Button size="small" startIcon={<LinkIcon />} href={recipe.sourceUrl} target="_blank" rel="noopener noreferrer" sx={{ mb: 1.5, p: 0 }}>
              View Original Recipe
            </Button>
          )}

          {/* Ingredients */}
          {recipe.ingredients && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>Ingredients</Typography>
              <Paper elevation={0} sx={{ bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1, p: 1.5, mt: 0.5 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
                  {recipe.ingredients}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Instructions */}
          {recipe.instructions && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>Instructions</Typography>
              <Paper elevation={0} sx={{ bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1, p: 1.5, mt: 0.5 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
                  {recipe.instructions}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Review section */}
          {reviewing ? (
            <Box sx={{ mt: 1, p: 2, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                {recipe.made ? 'Update your review' : 'How did it turn out?'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">Rating:</Typography>
                <Rating value={reviewRating} onChange={(_, v) => setReviewRating(v)} />
              </Box>
              <TextField fullWidth multiline rows={3} label="Notes & Review"
                value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                placeholder="How did it taste? Any tweaks you'd make next time?" sx={{ mb: 1.5 }} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" onClick={saveReview} size="small">Save Review</Button>
                <Button onClick={() => setReviewing(false)} size="small">Cancel</Button>
              </Box>
            </Box>
          ) : (
            <>
              {recipe.made && recipe.review && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'rgba(102,187,106,0.06)', borderRadius: 2, border: '1px solid rgba(102,187,106,0.15)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="overline" sx={{ color: '#66bb6a', fontWeight: 600 }}>My Review</Typography>
                    <Button size="small" onClick={openReview} sx={{ fontSize: '0.7rem', p: '2px 6px', minWidth: 0 }}>Edit</Button>
                  </Box>
                  {recipe.rating > 0 && <Rating value={recipe.rating} readOnly size="small" sx={{ mb: 0.5 }} />}
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{recipe.review}</Typography>
                  {recipe.madeDate && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Made on {recipe.madeDate}
                    </Typography>
                  )}
                </Box>
              )}
              <Box sx={{ mt: 1.5 }}>
                <Button size="small" variant={recipe.made ? 'outlined' : 'contained'}
                  startIcon={<CheckCircleIcon />} onClick={openReview}
                  sx={recipe.made ? {} : { background: 'linear-gradient(135deg, #00897b, #00acc1)' }}>
                  {recipe.made ? 'Update Review' : 'Mark as Made'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Collapse>

      {/* Context menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { onEdit(recipe); setMenuAnchor(null); }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit Recipe
        </MenuItem>
        {!reviewing && (
          <MenuItem onClick={() => { openReview(); setMenuAnchor(null); }}>
            <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />
            {recipe.made ? 'Update Review' : 'Mark as Made'}
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { onDelete(recipe.id); setMenuAnchor(null); }} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </Paper>
  );
};

// ─── Main RecipeList ──────────────────────────────────────────────────────────

const RecipeList = ({ list, onUpdateList }) => {
  const [recipes, setRecipes] = useState(list.items || []);
  const [filter, setFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);

  const save = (newRecipes) => {
    setRecipes(newRecipes);
    onUpdateList(list.id, { items: newRecipes });
  };

  const handleAdd = (data) => {
    save([...recipes, { id: uid(), favorite: false, made: false, rating: 0, review: '', madeDate: '', ...data }]);
    setDialogOpen(false);
  };

  const handleEdit = (data) => {
    save(recipes.map(r => r.id === editingRecipe.id ? { ...r, ...data } : r));
    setEditingRecipe(null);
  };

  const handleUpdate = (updated) => {
    save(recipes.map(r => r.id === updated.id ? updated : r));
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this recipe?')) save(recipes.filter(r => r.id !== id));
  };

  const filtered = recipes.filter(r => {
    if (filter === 'todo') return !r.made;
    if (filter === 'made') return r.made;
    if (filter === 'favorites') return r.favorite;
    return true;
  });

  // Stats
  const madeCount = recipes.filter(r => r.made).length;
  const favCount = recipes.filter(r => r.favorite).length;
  const avgRating = recipes.filter(r => r.rating > 0).reduce((a, r, _, arr) => a + r.rating / arr.length, 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{list.name}</Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">{recipes.length} recipes</Typography>
            {madeCount > 0 && <Typography variant="body2" color="text.secondary">· {madeCount} made</Typography>}
            {favCount > 0 && <Typography variant="body2" color="text.secondary">· {favCount} ❤️</Typography>}
            {avgRating > 0 && <Typography variant="body2" color="text.secondary">· avg {avgRating.toFixed(1)} ⭐</Typography>}
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingRecipe(null); setDialogOpen(true); }}>
          Add Recipe
        </Button>
      </Box>

      {/* Filter tabs */}
      <Tabs value={filter} onChange={(_, v) => setFilter(v)} sx={{ mb: 3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Tab value="all" label={`All (${recipes.length})`} />
        <Tab value="todo" label={`To Make (${recipes.filter(r => !r.made).length})`} />
        <Tab value="made" label={`Made (${madeCount})`} />
        <Tab value="favorites" label={`Favourites (${favCount})`} />
      </Tabs>

      {/* Recipe list */}
      {filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <RestaurantMenuIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">
            {filter === 'all' ? 'No recipes yet. Add your first one!' :
             filter === 'todo' ? 'All recipes have been made!' :
             filter === 'made' ? "You haven't made any recipes yet." :
             "No favourites yet. Heart a recipe to save it here."}
          </Typography>
          {filter === 'all' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)} sx={{ mt: 2 }}>
              Add Recipe
            </Button>
          )}
        </Box>
      ) : (
        filtered.map(recipe => (
          <RecipeCard
            key={recipe.id}
            recipe={recipe}
            onUpdate={handleUpdate}
            onEdit={(r) => { setEditingRecipe(r); setDialogOpen(true); }}
            onDelete={handleDelete}
          />
        ))
      )}

      {/* Add/Edit dialog */}
      <RecipeDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingRecipe(null); }}
        onSave={editingRecipe ? handleEdit : handleAdd}
        existing={editingRecipe}
      />
    </Box>
  );
};

export default RecipeList;
